import { useQuery } from '@apollo/client';
import { FC, useCallback, useMemo, useState } from 'react';
import { v4 } from 'uuid';

import type {
  BulkEntityFilters,
  BulkEntitySelectionModeType,
} from '@eluve/api-contract';
import { BulkEntitySelectionMode as SelectionMode } from '@eluve/api-contract';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Heading,
  Icon,
  ProgressPanel,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useProgressPanelController,
} from '@eluve/components';
import { formatErrorForLogging } from '@eluve/eluve-errors';
import { appointmentNameFragment } from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';
import { useNamedLogger } from '@eluve/logger';
import { useAnalytics } from '@eluve/posthog-react';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';
import { useDialog } from '@eluve/utility-hooks';
import { pluralize } from '@eluve/utils';

import { useCreateBulkPdfZipUrl } from './useBulkGetChartsPdfUrl';

const getFailedAppointmentsByIdsQuery = graphql(
  `
    query getFailedAppointmentsByIds($tenantId: uuid!, $ids: [uuid!]!) {
      appointments(where: { tenantId: { _eq: $tenantId }, id: { _in: $ids } }) {
        ...AppointmentName
      }
    }
  `,
  [appointmentNameFragment],
);

interface FailedAppointmentsDetailsProps {
  failedAppointmentIds: string[];
  tenantId: string;
}

const FailedAppointmentsDetails: FC<FailedAppointmentsDetailsProps> = ({
  failedAppointmentIds,
  tenantId,
}) => {
  const failedCount = failedAppointmentIds.length;

  const { data, loading } = useQuery(getFailedAppointmentsByIdsQuery, {
    variables: {
      tenantId,
      ids: failedAppointmentIds,
    },
    skip: failedAppointmentIds.length === 0,
  });

  const appointmentNameMap = useMemo(() => {
    const map = new Map<string, string>();
    data?.appointments?.forEach((appointment) => {
      if (appointment?.id && appointment?.name) {
        map.set(appointment.id, appointment.name);
      }
    });
    return map;
  }, [data]);

  if (failedAppointmentIds.length === 0) {
    return null;
  }

  return (
    <Stack gap={5} className="max-h-[300px] overflow-y-auto">
      <Stack
        gap={3}
        className="rounded-lg border border-border-warning-subtle bg-bg-warning-subtle p-3 text-text-warning-subtle"
        direction="horizontal"
      >
        <Icon name="AlertTriangle" />
        <Text inline weight="medium" color="inherit">
          {pluralize(failedCount, 'appointment')} couldn't be downloaded
        </Text>
      </Stack>
      <Stack gap={2}>
        <Text weight="medium" color="primary">
          The following {pluralize(failedCount, 'appointment')} could not be
          exported:
        </Text>
        <Stack gap={1} className="pl-2">
          {loading ? (
            <Text weight="medium" color="primary">
              Loading...
            </Text>
          ) : (
            failedAppointmentIds.map((appointmentId) => {
              const appointmentName =
                appointmentNameMap.get(appointmentId) ?? appointmentId;
              return (
                <Text
                  key={appointmentId}
                  size="sm"
                  weight="medium"
                  color="primary"
                >
                  • {appointmentName}
                </Text>
              );
            })
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

const ErrorDialog: FC<{
  isOpen: boolean;
  onClose: () => void;
  errorMessage: string | null;
}> = ({ isOpen, onClose, errorMessage }) => {
  const message =
    errorMessage ??
    "The appointments you selected can't be exported. Make sure they have available summaries.";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent size="xl" className="p-6">
        <DialogHeader>
          <DialogTitle className="text-center">
            <Heading size="2xl">Appointments can't be exported to PDF</Heading>
          </DialogTitle>
        </DialogHeader>
        <Stack align="center" gap={2}>
          <Text weight="regular" color="secondary">
            {message}
          </Text>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export interface DownloadChartsPdfButtonProps {
  tenantId: string;
  appointmentIds: string[];
  selectionMode: BulkEntitySelectionModeType;
  filters?: BulkEntityFilters;
  disabled?: boolean;
}

export const DownloadChartsPdfButton = ({
  tenantId,
  appointmentIds,
  selectionMode,
  filters,
  disabled,
}: DownloadChartsPdfButtonProps) => {
  const { job, start, succeed, hide } = useProgressPanelController();

  const logger = useNamedLogger('DownloadChartsPdfButton');
  const analytics = useAnalytics('bulk_chart_export');
  const isBulkChartExportEnabled = useIsFeatureFlagEnabled('BULK_CHART_EXPORT');
  const createBulkPdfZipUrl = useCreateBulkPdfZipUrl({ tenantId });
  const {
    isDialogOpen: isErrorDialogOpen,
    openDialog: openErrorDialog,
    closeDialog: closeErrorDialog,
  } = useDialog(false);

  const [isDownloadingCharts, setIsDownloadingCharts] = useState(false);
  const [failedAppointmentIds, setFailedAppointmentIds] = useState<string[]>(
    [],
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleDownloadChartsPdf = useCallback(async () => {
    if (
      selectionMode === SelectionMode.EXPLICIT &&
      appointmentIds.length === 0
    ) {
      logger.warn('No appointments selected');
      return;
    }

    if (selectionMode === SelectionMode.FILTER && !filters) {
      logger.warn('No filters provided');
      return;
    }

    const eventId = v4();
    analytics.capture('export_attempt', {
      eventId,
      tenantId,
      ...(selectionMode === SelectionMode.EXPLICIT && {
        totalAppointmentCount: appointmentIds.length,
      }),
      selectionMode,
    });

    setIsDownloadingCharts(true);
    setFailedAppointmentIds([]);
    setErrorMessage(null);
    start({
      title: 'Preparing download',
      detail:
        selectionMode === SelectionMode.EXPLICIT
          ? `Downloading ${appointmentIds.length} charts`
          : 'Downloading charts',
    });

    try {
      const result = await createBulkPdfZipUrl(
        selectionMode === SelectionMode.EXPLICIT
          ? {
              selectionMode: SelectionMode.EXPLICIT,
              appointmentIds,
            }
          : {
              selectionMode: SelectionMode.FILTER,
              filters: filters ?? {}, // validated by the empty filters check above
            },
      );
      logger.debug('Bulk PDF zip result', { result });

      if (!result.zipUrl) {
        throw new Error('Zip URL not available');
      }

      const link = document.createElement('a');
      link.href = result.zipUrl;
      document.body.appendChild(link);
      link.download = ''; // let browser infer filename
      link.click();
      document.body.removeChild(link);

      if (
        result.failedAppointmentIds &&
        result.failedAppointmentIds.length > 0
      ) {
        setFailedAppointmentIds(result.failedAppointmentIds);
      } else {
        setFailedAppointmentIds([]);
      }

      succeed(
        'Download complete',
        `${pluralize(result.successCount, 'chart')} downloaded`,
      );

      analytics.capture('export_success', {
        eventId,
        tenantId,
        totalAppointmentCount: result.totalCount,
        successCount: result.successCount,
        failedCount: result.failedAppointmentIds?.length ?? 0,
        selectionMode,
      });
    } catch (error) {
      const message = formatErrorForLogging(error);
      analytics.capture('export_failed', {
        eventId,
        tenantId,
        error: message,
        selectionMode,
      });
      logger.error('Failed to download charts', { error });

      hide();
      setErrorMessage(message);
      openErrorDialog();
    } finally {
      setIsDownloadingCharts(false);
    }
  }, [
    appointmentIds,
    selectionMode,
    filters,
    createBulkPdfZipUrl,
    logger,
    analytics,
    start,
    succeed,
    hide,
    openErrorDialog,
    tenantId,
  ]);

  if (
    !isBulkChartExportEnabled ||
    (selectionMode === SelectionMode.EXPLICIT && appointmentIds.length === 0) ||
    (selectionMode === SelectionMode.FILTER && !filters)
  ) {
    return null;
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="sm"
            onClick={handleDownloadChartsPdf}
            loading={isDownloadingCharts}
            disabled={disabled}
          >
            <Icon name="Download" size="auto" />
            Download Charts as PDF
          </Button>
        </TooltipTrigger>
        {disabled && (
          <TooltipContent>
            Selected appointments don't have available summaries
          </TooltipContent>
        )}
      </Tooltip>
      <ProgressPanel job={job} onClose={hide}>
        {job?.status === 'success' && failedAppointmentIds.length > 0 && (
          <FailedAppointmentsDetails
            failedAppointmentIds={failedAppointmentIds}
            tenantId={tenantId}
          />
        )}
      </ProgressPanel>
      <ErrorDialog
        isOpen={isErrorDialogOpen}
        onClose={closeErrorDialog}
        errorMessage={errorMessage}
      />
    </>
  );
};
